import { motion, AnimatePresence } from 'framer-motion'
import { X, MessageCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { colors } from '@/styles/colors'
import CommentThread from './CommentThread'
import { useTranslation } from '@/i18n'
import { getCurrentDirection } from '@/i18n/config'

interface CommentPanelProps {
  workspaceId: string
  isOpen: boolean
  onClose: () => void
}

const CommentPanel = ({ workspaceId, isOpen, onClose }: CommentPanelProps) => {
  const { t } = useTranslation()
  const isRTL = getCurrentDirection() === 'rtl'
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/20 z-40"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: isRTL ? '-100%' : '100%' }}
            animate={{ x: 0 }}
            exit={{ x: isRTL ? '-100%' : '100%' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className={`fixed top-0 h-full w-[400px] shadow-xl z-50 flex flex-col bg-white dark:bg-dark-card ${isRTL ? 'left-0' : 'right-0'}`}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-border"
            >
              <div className="flex items-center gap-2">
                <MessageCircle size={20} style={{ color: colors.blue[600] }} />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
                  {t('board:comments.title')}
                </h2>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="hover:bg-gray-100 dark:hover:bg-dark-accent"
              >
                <X size={20} />
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <CommentThread workspaceId={workspaceId} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default CommentPanel
